'use client';

import { useMemo, useCallback } from 'react';
import ReactFlow, { Background, Controls, useNodesState, useEdgesState } from 'reactflow';
import type { Node, Edge } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Paper, Avatar } from '@mui/material';
import { useNode } from '@diggens/shared-hooks';

interface OrgChartSectionProps {
  organizationId: string;
}

export function OrgChartSection({ organizationId }: OrgChartSectionProps) {
  const { data: organization } = useNode(organizationId);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Build org chart from organization relationships
  const orgChartNodes = useMemo(() => {
    if (!organization) return { nodes: [], edges: [] };

    const orgNodes: Node[] = [];
    const orgEdges: Edge[] = [];

    // Add organization node at top
    orgNodes.push({
      id: organizationId,
      type: 'default',
      position: { x: 250, y: 0 },
      data: {
        label: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{(organization.props.name as string)?.charAt(0) || 'O'}</Avatar>
            <Box>
              <Typography variant="subtitle2">{organization.props.name as string}</Typography>
              <Typography variant="caption" color="text.secondary">
                Organization
              </Typography>
            </Box>
          </Box>
        ),
      },
    });

    // TODO: Add related people nodes based on relationships
    // This would query for people connected to this organization
    // and create hierarchical nodes below the org

    return { nodes: orgNodes, edges: orgEdges };
  }, [organization, organizationId]);

  // Update nodes and edges when org chart changes
  useMemo(() => {
    if (orgChartNodes.nodes) {
      setNodes(orgChartNodes.nodes);
      setEdges(orgChartNodes.edges);
    }
  }, [orgChartNodes, setNodes, setEdges]);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    // TODO: Handle node click to navigate to person/org details
    console.log('Org chart node clicked:', node.id);
  }, []);

  if (!organization) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading organization chart...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: 400 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Organization Chart
      </Typography>
      <Paper variant="outlined" sx={{ height: 350, position: 'relative' }}>
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} fitView attributionPosition="bottom-left">
          <Background />
          <Controls />
        </ReactFlow>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Showing people and relationships for {organization.props.name as string}
      </Typography>
    </Box>
  );
}
